import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refreshToken'));
  const [loading, setLoading] = useState(true);
  const isRefreshing = useRef(false);
  const failedQueue = useRef([]);

  // Process queued requests after token refresh
  const processQueue = (error, newToken = null) => {
    failedQueue.current.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(newToken);
      }
    });
    failedQueue.current = [];
  };

  // Create api instance once (stable across renders) with interceptors attached once.
  // Re-creating the axios instance on every render caused consumers'
  // useEffect([..., api]) hooks to re-fire constantly, triggering cart/course
  // refetches that clobbered state between clicks (first click would fail,
  // second would succeed once state settled).
  const apiRef = useRef(null);
  if (apiRef.current === null) {
    // `withCredentials: true` is REQUIRED so Django's sessionid cookie is
    // carried across the axios POST (add-to-cart) → GET (/cart/) pair.
    // Without it, guest users always see an empty cart after submitting
    // the guest form because each request opens a fresh empty session.
    const instance = axios.create({ baseURL: `${API_URL}/api`, withCredentials: true });

    instance.interceptors.request.use((config) => {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    });

    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (isRefreshing.current) {
            return new Promise((resolve, reject) => {
              failedQueue.current.push({ resolve, reject });
            }).then(newToken => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return instance(originalRequest);
            }).catch(err => Promise.reject(err));
          }

          originalRequest._retry = true;
          isRefreshing.current = true;

          const storedRefreshToken = localStorage.getItem('refreshToken');

          if (!storedRefreshToken) {
            processQueue(error, null);
            isRefreshing.current = false;
            handleLogout();
            return Promise.reject(error);
          }

          try {
            const response = await axios.post(`${API_URL}/api/auth/refresh`, {
              refresh: storedRefreshToken
            });

            const { access_token, refresh_token: newRefresh } = response.data;

            localStorage.setItem('token', access_token);
            if (newRefresh) {
              localStorage.setItem('refreshToken', newRefresh);
              setRefreshToken(newRefresh);
            }
            setToken(access_token);

            processQueue(null, access_token);
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            return instance(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError, null);
            handleLogout();
            return Promise.reject(refreshError);
          } finally {
            isRefreshing.current = false;
          }
        }

        return Promise.reject(error);
      }
    );

    apiRef.current = instance;
  }
  const api = apiRef.current;

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  };

  const fetchUser = useCallback(async () => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Only clear if it's an auth error and refresh also failed
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Token expiry check - refresh proactively before expiry
  useEffect(() => {
    if (!token) return;

    const checkTokenExpiry = () => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiry = payload.exp * 1000;
        const now = Date.now();
        const timeUntilExpiry = expiry - now;
        
        // Refresh 5 minutes before expiry
        if (timeUntilExpiry < 5 * 60 * 1000 && timeUntilExpiry > 0) {
          const storedRefresh = localStorage.getItem('refreshToken');
          if (storedRefresh && !isRefreshing.current) {
            isRefreshing.current = true;
            axios.post(`${API_URL}/api/auth/refresh`, { refresh: storedRefresh })
              .then(response => {
                const { access_token, refresh_token: newRefresh } = response.data;
                localStorage.setItem('token', access_token);
                if (newRefresh) localStorage.setItem('refreshToken', newRefresh);
                setToken(access_token);
                if (newRefresh) setRefreshToken(newRefresh);
              })
              .catch(() => {
                // Silent fail - will retry on next interval or request
              })
              .finally(() => {
                isRefreshing.current = false;
              });
          }
        }
      } catch (e) {
        // Invalid token format
      }
    };

    // Check immediately and then every minute
    checkTokenExpiry();
    const interval = setInterval(checkTokenExpiry, 60 * 1000);
    return () => clearInterval(interval);
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
      setRefreshToken(refresh_token);
    }
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, userData);
    const { access_token, refresh_token, user: newUser } = response.data;
    
    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
      setRefreshToken(refresh_token);
    }
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    handleLogout();
  };

  const requestPasswordReset = async (email) => {
    await axios.post(`${API_URL}/api/auth/password-reset-request`, null, { params: { email } });
  };

  /** Adopt a JWT pair returned by a backend flow (e.g. /claim-account) so
   * the SPA is authenticated immediately, without a second /login round-trip. */
  const setAuthFromTokens = ({ access, refresh, user: userData }) => {
    if (access) {
      localStorage.setItem('token', access);
      setToken(access);
    }
    if (refresh) {
      localStorage.setItem('refreshToken', refresh);
      setRefreshToken(refresh);
    }
    if (userData) setUser(userData);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    requestPasswordReset,
    setAuthFromTokens,
    refreshUser: fetchUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isInstructor: user?.role === 'instructor',
    isVolunteer: user?.role === 'volunteer',
    isParent: user?.role === 'parent',
    isPartner: user?.role === 'partner',
    isStudent: user?.role === 'student',
    userRole: user?.role,
    api,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
